import React, {MouseEventHandler, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router-dom'
import Checkbox from '@material-ui/core/Checkbox'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import {Menu} from '@material-ui/icons'
import {SearchedIndividualEEG} from 'api/analysisApi'

import {Roles} from 'Authority'
import ActionButton from 'components/atoms/Buttons/ActionButton'
import TableButton from 'components/atoms/Buttons/TableButton'
import {ClientName} from 'components/atoms/ClientName'
import CompanySearch from 'components/molcules/CompanySearch'
import Pagination from 'components/molcules/Pagination'
import TableCell from 'components/Table/TableCell'
import TableCellShortStatus from 'components/Table/TableCellShortStatus'
import TableReportSearch from 'components/Table/TableReportSearch'
import useStyles from 'components/Table/useTableStyles'
import useToolbarStyles from 'components/Table/useTableToolbarStyles'
import {AnalysisReportType} from 'constants/AnalysisConstant'
import {getEegIndividualReportPath} from 'constants/RouteConstant'
import {useIcaSelectDialog} from 'features/analysis/useEegIndividualIcaDialog'
import useEegIndividualSearch from 'features/analysis/useEegIndividualSearch'
import useAuth from 'features/auth/useAuth'
import {
  getEyeStatus,
  isReadable,
  isReadDisable,
  statusToI18nString,
} from 'helpers/analysisHelper'
import {
  dateToPeriodString,
  getDefaultPeriodDate,
  isoStringToDateAndTimeString,
} from 'helpers/dateHelper'
import {getQueryFromUrl} from 'helpers/commonHelper'
import {openInNewTab} from 'helpers/openInNewTab'
import {
  addPushReceiveHandler,
  removePushReceiveHandler,
} from 'helpers/pushHelper'
import {TYPE_INDICATOR_PRODUCT} from 'pages/ProductsPages/constant'
import useProductManagement from 'features/productManagement/useProductManagement'
import ReanalysisDialog from './ReanalysisDialog'

interface TableToolBarProps {
  selectedId: number
  selectedIndividual: SearchedIndividualEEG | undefined
}

function TableToolbar({selectedId, selectedIndividual}: TableToolBarProps) {
  const {t} = useTranslation()
  const location = useLocation()
  const classes = useToolbarStyles()
  const {query, onSearch} = useEegIndividualSearch()
  const {onOpen: onIcaSelectDialogOpen} = useIcaSelectDialog()
  const [isShowReanalysisDialog, setIsShowReanalysisDialog] = useState(false)

  const [selection] = React.useState<PeriodDate>(getDefaultPeriodDate())

  const {user: currentUser} = useAuth()
  const userAuthority = currentUser?.authority ?? ''
  const isMaster = userAuthority === Roles.ROLE_COMPANY_MASTER
  const handleOrganizationSearch = () => {}

  /** Set default value for search query if it is stored in url */
  useEffect(() => {
    const initValue = getQueryFromUrl(location.search) || {}
    onSearch({
      ...query,
      search: {searchName: 'NAME', searchValue: ''},
      period: dateToPeriodString(selection),
      paging: {page: 0, size: 10},
      ...initValue,
    })
  }, [])

  return (
    <div className={classes.root}>
      <TableReportSearch
        reportType={AnalysisReportType.EEGIndividual}
        isRoot={false}
        query={query}
        onSearch={onSearch}
      >
        {isMaster && <CompanySearch onSearch={handleOrganizationSearch} />}
      </TableReportSearch>
      <div className={classes.containerOptional}>
        <ActionButton
          onClick={() => onIcaSelectDialogOpen(selectedId)}
          className={classes.reAnalysisButton}
          disabled={!selectedId}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Menu />
            {t('IReAnalysis')}
          </div>
        </ActionButton>
        <ActionButton
          onClick={() => setIsShowReanalysisDialog(true)}
          className={classes.reAnalysisButton}
          disabled={!selectedId}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 5,
            }}
          >
            {t('IAnalysisReports')}
          </div>
        </ActionButton>
      </div>
      {isShowReanalysisDialog && (
        <ReanalysisDialog
          open={isShowReanalysisDialog}
          onClose={() => setIsShowReanalysisDialog(false)}
          selectedIndividual={selectedIndividual as SearchedIndividualEEG}
        />
      )}
    </div>
  )
}

interface RenderTableRowProps {
  item: SearchedIndividualEEG
  onClick: (item: SearchedIndividualEEG) => void
  isSelected: (item: SearchedIndividualEEG) => boolean
}

const getAnalysisReportPath = (
  status: AnalysisStatus,
  id: number,
  version?: string,
  clientName?: string,
) => {
  if (status === 'SUCCESS')
    return getEegIndividualReportPath(id, version, clientName)
  if (status === 'ICA')
    return getEegIndividualReportPath(id, undefined, clientName)
  if (status === 'TIME_REJECTION')
    return getEegIndividualReportPath(id, undefined, clientName)
  return '/'
}

function RenderTableRow({item, onClick, isSelected}: RenderTableRowProps) {
  const {t} = useTranslation()
  const handleResultClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    const clientName = {
      firstName: item.firstName ?? '',
      lastName: item.lastName ?? '',
    }

    const reportPath = getAnalysisReportPath(
      item.progress,
      item.id,
      item.normdbVersion,
      encodeURIComponent(JSON.stringify(clientName)),
    )
    openInNewTab(reportPath)
  }

  const labelId = `enhanced-table-checkbox-${item.id}`

  const checked = isSelected(item)

  const isReportReadable = isReadable(item.progress)

  const handleClick = () => onClick(item)

  const getStatus = () => {
    if (item.reAnalysisId === '-') {
      return t(statusToI18nString(item.progress))
    }

    if (item.progress === 'ICA' || item.progress === 'TIME_REJECTION') {
      return t(statusToI18nString(item.progress))
    }

    return `${t(statusToI18nString(item.progress))}`
  }

  return (
    <TableRow hover onClick={handleClick}>
      <TableCell padding='checkbox'>
        <Checkbox
          checked={checked}
          disabled={isReadDisable(item.progress)}
          inputProps={{'aria-labelledby': labelId}}
        />
      </TableCell>
      <TableCell align='center'>{item.id}</TableCell>
      <TableCell align='center'>{item.reAnalysisId}</TableCell>
      <TableCell align='center'>
        <ClientName
          firstName={item.firstName}
          lastName={item.lastName}
          birth={item.birth}
          gender={item.gender}
        />
      </TableCell>
      <TableCell align='center'>{getEyeStatus(item.eceo)}</TableCell>
      <TableCell align='center'>{item.fileName}</TableCell>
      <TableCell align='center'>
        {isoStringToDateAndTimeString(item.createdAt)}
      </TableCell>
      <TableCell align='center'>{item.normdbVersion || '-'}</TableCell>
      <TableCell align='center'>{getStatus()}</TableCell>
      <TableCellShortStatus align='center' status={item.normStatus === 'o'} />
      <TableCellShortStatus
        align='center'
        status={item.mciStatus === 'o' || item.mci8Status === 'o'}
      />
      <TableCellShortStatus
        align='center'
        status={
          item.summaryStatus === 'o' ||
          item.fiveIndicatorStatus === 'o' ||
          item.brainConnectivityStatus === 'o'
        }
      />

      <TableCell align='center'>
        {t(statusToI18nString(item.additionalAnalysis))}
      </TableCell>
      <TableCell align='center'>
        <TableButton onClick={handleResultClick} disabled={!isReportReadable}>
          {t('IRead')}
        </TableButton>
      </TableCell>
    </TableRow>
  )
}

interface PageTableProps {
  selectedOld: SearchedIndividualEEG[]
  setSelectedOld: (
    data: SearchedIndividualEEG[],
    items: SearchedIndividualEEG[],
  ) => void
}

function PageTable(props: PageTableProps) {
  const {selectedOld, setSelectedOld} = props
  const {t} = useTranslation()
  const classes = useStyles()

  const {loading, query, paging, pagingInfo, items, onSearch} =
    useEegIndividualSearch()

  const [selected, setSelected] = useState<SearchedIndividualEEG[]>([])
  const {getAvailableLicenses, getPackageList} = useProductManagement()

  const selectedIds = useMemo(() => selected.map((item) => item.id), [selected])
  const [selectedId, setSelectedId] = useState<number>(0)
  const selectedIndividual = useMemo(
    () => items.find((item) => item.id === selectedId),
    [items, selectedId],
  )

  const setPageIndex = (page: number) => {
    setSelectedOld(selected, items)
    onSearch({
      ...query,
      paging: {
        page,
        size: paging.size,
      },
    })
  }

  const handleRowClick = (item: SearchedIndividualEEG) => {
    if (!isReadable(item.progress)) {
      return
    }

    const selectedIndex = selectedIds.indexOf(item.id)

    let newSelected: SearchedIndividualEEG[] = []

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, item)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      )
    }

    setSelected(newSelected)

    if (selectedId === item.id) setSelectedId(0)
    else setSelectedId(item.id)
  }

  const isRowSelected = (item: SearchedIndividualEEG) => {
    return selectedId === item.id
  }
  const emptyRows = items === null ? 5 : 5 - items.length

  const onSizeChange = (
    event: React.ChangeEvent<{name?: string; value: unknown}>,
  ) => {
    const size = parseInt(event.target.value as string, 10)
    onSearch({...query, paging: {page: 0, size}})
  }

  useEffect(() => {
    const handler = (event: any) => {
      if (event.detail.data.status) {
        onSearch({...query, paging: {page: 0, size: 10}})
      }
    }
    getAvailableLicenses()
    getPackageList({
      target: TYPE_INDICATOR_PRODUCT.ALL,
    })
    addPushReceiveHandler(handler)
    return () => removePushReceiveHandler(handler)
  }, [])

  useEffect(() => {
    const itemIds = items.map((item) => item.id)
    const selectedOldNew = []
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < selectedOld.length; i++) {
      if (itemIds.indexOf(selectedOld[i].id) !== -1) {
        selectedOldNew.push(selectedOld[i])
      }
    }

    setSelected(selectedOldNew)
  }, [selectedOld, items])

  return (
    <div>
      <TableContainer className={classes.tableContainer}>
        <TableToolbar
          selectedId={selectedId}
          selectedIndividual={selectedIndividual}
        />
        <Table className={classes.table} size='small'>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell align='center' padding='none' />
              <TableCell align='center' padding='none'>
                {t('IEEGIndividualIndex')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('IReAnalysis_no')}
              </TableCell>
              <TableCell
                align='center'
                padding='none'
                style={{minWidth: '100px'}}
              >
                {t('IPatientName')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('IECEO')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('IFileName')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('IMeasureDate')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('IVersion')}
              </TableCell>
              <TableCell
                align='center'
                padding='none'
                style={{minWidth: '100px'}}
              >
                {t('IProgress')}
              </TableCell>
              <TableCell
                align='center'
                padding='none'
                style={{minWidth: '100px'}}
              >
                {t('INormDB')}
              </TableCell>
              <TableCell
                align='center'
                padding='none'
                style={{minWidth: '100px'}}
              >
                {t('IAMci')}
              </TableCell>
              <TableCell
                align='center'
                padding='none'
                style={{minWidth: '100px'}}
              >
                {t('ISummaryRun')}
              </TableCell>
              <TableCell
                align='center'
                padding='none'
                style={{minWidth: '100px'}}
              >
                {t('IView3D')}
              </TableCell>
              <TableCell align='center' padding='none'>
                {t('IAnalysisReport')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <RenderTableRow
                key={item.id}
                item={item}
                onClick={handleRowClick}
                isSelected={isRowSelected}
              />
            ))}
            {emptyRows > 0 && (
              <TableRow
                style={{height: 43 * emptyRows, backgroundColor: '#F9F9FB'}}
              >
                <TableCell colSpan={14} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Pagination
          totalPageCount={pagingInfo.totalPages}
          currentPageIndex={query.paging.page ?? 0}
          itemCountPerPage={query.paging.size ?? 10}
          setCurrentPageIndex={setPageIndex}
          loading={loading}
          onItemCountPerPageChanged={onSizeChange}
        />
      </TableContainer>
    </div>
  )
}

export default PageTable
